import React from 'react';
import { Link } from 'react-router-dom';
import SeoMeta from '../components/SeoMeta';
import ArticleLayout from '../components/articles/ArticleLayout';
import { Callout, Checks, DataTable, ArticleLink } from '../components/articles/blocks';
import { FaqList, faqSchema } from '../components/common/B2B';

const FAQ = [
  {
    q: '¿Puedo usar un solo documento para todo?',
    a: 'Sí, con plataformas como BlueCMR puede generar un documento digital único que cumple las funciones de DeCA y Carta de Porte para transportes nacionales, ahorrando tiempo y reduciendo errores.',
  },
  {
    q: '¿Qué pasa si no llevo el DeCA?',
    a: 'No llevarlo, o llevarlo en papel o incompleto, se sanciona conforme al régimen sancionador de la LOTT. Es obligatorio llevarlo a bordo, en formato electrónico desde el 5 de octubre de 2026.',
  },
  {
    q: '¿El eCMR es válido en todos los países?',
    a: 'El eCMR es válido en los países que han ratificado el Protocolo Adicional al Convenio CMR (2008). A noviembre de 2026 lo han ratificado 42 Partes, entre ellas España, Francia, Alemania, Italia y Portugal. Consulte la lista actualizada antes de viajar.',
  },
  {
    q: '¿Desde cuándo es obligatorio el DeCA digital?',
    a: 'Desde el 5 de octubre de 2026, el DeCA es obligatoriamente digital en España según la Ley 9/2025 de Movilidad Sostenible. Debe cumplir los requisitos técnicos de la Resolución de 5/06/2026: formato PDF nativo digital (máximo 5 MB) con código QR y URL HTTPS de descarga directa.',
  },
  {
    q: '¿Necesito firmas en el DeCA?',
    a: 'No, el DeCA es informativo para inspecciones y no requiere firmas. Sin embargo, la Carta de Porte y el CMR sí requieren firmas de cargador y porteador para formalizar el contrato. El destinatario firma posteriormente como acuse de recibo de la mercancía (Art. 12 Ley 15/2009).',
  },
  {
    q: '¿Qué es el Reglamento eFTI y cómo afecta?',
    a: 'El Reglamento (UE) 2020/1056 (eFTI) establece un marco común para el intercambio electrónico de información de transporte con las autoridades, que deberán aceptarla desde julio de 2027 a través de plataformas eFTI certificadas; la certificación de plataformas todavía no se ha abierto. Conviene elegir soluciones que ya trabajen con datos estructurados en formato eFTI para evitar migraciones cuando se aplique plenamente.',
  },
];

const DiferenciasDocumentosPage = () => {
  return (
    <>
      <SeoMeta
        title="Diferencias entre Documento de Control, Carta de Porte y CMR/eCMR | BlueCMR"
        description="Diferencias entre el DeCA (documento de control), la carta de porte y el CMR/eCMR: naturaleza, ámbito, firmas y obligatoriedad de cada uno."
        canonical="https://bluecmr.com/articulos/diferencias-entre-dca-carta-de-porte-y-cmr-ecmr"
        type="article"
        keywords="documento de control, carta de porte, CMR, eCMR, diferencias documentos transporte, transporte mercancías, documentos obligatorios transporte"
        breadcrumbs={[
          { name: 'Inicio', url: 'https://bluecmr.com' },
          { name: 'Artículos', url: 'https://bluecmr.com/articulos' },
          { name: 'Diferencias entre documentos de transporte', url: 'https://bluecmr.com/articulos/diferencias-entre-dca-carta-de-porte-y-cmr-ecmr' }
        ]}
        structuredData={[
          {
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: 'Diferencias entre DeCA (documento de control), Carta de Porte y CMR/eCMR',
            description: 'Comparativa completa de los principales documentos de transporte: naturaleza, ámbito, firmas obligatorias y más.',
            image: 'https://www.bluecmr.com/og/articulo-diferencias.jpg',
            datePublished: '2025-01-15',
            dateModified: '2026-11-04',
            author: {
              '@type': 'Organization',
              name: 'BlueCMR',
              url: 'https://bluecmr.com'
            },
            publisher: {
              '@type': 'Organization',
              name: 'BlueCMR',
              logo: {
                '@type': 'ImageObject',
                url: 'https://www.bluecmr.com/og/logo-bluecmr.png'
              }
            },
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': 'https://bluecmr.com/articulos/diferencias-entre-dca-carta-de-porte-y-cmr-ecmr'
            },
            articleSection: 'Guías',
            keywords: 'documento de control, carta de porte, CMR, eCMR, diferencias documentos transporte'
          },
          faqSchema(FAQ)
        ]}
      />
      <ArticleLayout
        url="/articulos/diferencias-entre-dca-carta-de-porte-y-cmr-ecmr"
        title="Diferencias entre el DeCA (documento de control), la Carta de Porte y el CMR/eCMR"
        deck="Guía para entender las diferencias, similitudes y posibilidades de integración entre los principales documentos de transporte."
        updated="2026-11-04"
        readingMinutes={7}
        toc={[
          { id: 'que-es-cada-documento', label: '¿Qué es cada documento?' },
          { id: 'como-se-relacionan', label: '¿Cómo se relacionan estos documentos?' },
          { id: 'casos-practicos', label: 'Casos prácticos' },
          { id: 'tabla-comparativa', label: 'Tabla comparativa completa' },
          { id: 'preguntas-frecuentes', label: 'Preguntas frecuentes' },
        ]}
        closing={{ title: '¿Necesita una solución que le permita generar estos documentos?', text: 'Le enseñamos cómo los genera BlueCMR en su operativa, sin compromiso.' }}
      >
        <h2 id="que-es-cada-documento">¿Qué es cada documento?</h2>

        <h3>DeCA (Documento de Control Digital)</h3>
        <p>
          El DeCA es un documento <strong>administrativo</strong> exigido por la Orden FOM/2861/2012 —que lo denomina "documento de control administrativo", de ahí la sigla DCA con la que también se le conocía— para el control e inspección del transporte de mercancías por carretera en España. Su finalidad es que las autoridades puedan verificar quién transporta qué, cuándo y dónde.
        </p>
        <p><strong>Características principales:</strong></p>
        <Checks
          items={[
            <><strong>Obligatorio</strong> (salvo excepciones) para los transportes de mercancías por carretera en España</>,
            'Debe viajar en la cabina del vehículo',
            <>Es <strong>obligatorio en formato digital</strong> (PDF nativo digital con código QR) desde el 5 de octubre de 2026 por la Ley de Movilidad Sostenible</>,
            'No requiere firmas (es informativo para inspección)',
          ]}
        />
        <Callout title="Requisitos técnicos del formato digital">
          <ul className="list-disc pl-5 space-y-1">
            <li>Formato <strong>PDF</strong> (máximo 5 MB)</li>
            <li>Código QR integrado que apunte a repositorio HTTPS</li>
            <li>Conservación mínima de 1 año (ambas partes)</li>
          </ul>
          <p>
            <Link to="/articulos/formato-pdfa1a-documento-control-digital-2026">Más información sobre requisitos técnicos</Link>
          </p>
        </Callout>
        <p>
          Piense en él como el "DNI del transporte": identifica quién, qué y dónde, pero no prueba la entrega ni el contrato.
        </p>
        <ArticleLink to="/documentos/documento-control" title="Más información sobre el DeCA" />

        <h3>Carta de Porte</h3>
        <p>
          Es un documento <strong>contractual</strong> que formaliza el contrato de transporte de mercancías por carretera en el ámbito nacional. Prueba que el transportista ha recibido la mercancía y se compromete a entregarla en destino.
        </p>
        <p><strong>Características principales:</strong></p>
        <Checks
          items={[
            <>Formaliza el <strong>contrato de transporte</strong> entre cargador y porteador (Art. 11, Ley 15/2009)</>,
            <>Requiere <strong>firmas de cargador y porteador</strong> (el destinatario firma al recibir la mercancía)</>,
            'Prueba la entrega de la mercancía',
            <>Válido para transporte <strong>nacional</strong></>,
          ]}
        />
        <p>
          Es como un "contrato firmado": demuestra que hubo acuerdo y que la mercancía se entregó.
        </p>
        <ArticleLink to="/documentos/carta-porte" title="Más información sobre la Carta de Porte Digital" />

        <h3>CMR / eCMR</h3>
        <p>
          Es el documento <strong>contractual internacional</strong> regulado por el Convenio CMR (1956) para el transporte de mercancías por carretera entre países. El eCMR es su versión digital, reconocida desde 2008.
        </p>
        <p><strong>Características principales:</strong></p>
        <Checks
          items={[
            <>Válido para transporte <strong>internacional</strong> (entre países firmantes del Convenio CMR)</>,
            'Debe viajar en cabina según Reglamento CE 1072/2009',
            <>Requiere <strong>firmas de expedidor y transportista</strong> (el destinatario firma al recibir)</>,
            'Regula responsabilidades y límites de indemnización',
          ]}
        />
        <p>
          Es el "pasaporte del transporte internacional": reconocido en múltiples países y con reglas claras de responsabilidad.
        </p>
        <ArticleLink to="/documentos/ecmr" title="Más información sobre el eCMR" />

        <h2 id="como-se-relacionan">¿Cómo se relacionan estos documentos?</h2>

        <h3>Transporte nacional</h3>
        <p>
          En un transporte nacional en España, necesita <strong>ambos documentos</strong>:
        </p>
        <ol>
          <li><strong>DeCA</strong> - Para cumplir con la Orden FOM/2861/2012 y permitir inspecciones</li>
          <li><strong>Carta de Porte</strong> - Para formalizar el contrato y probar la entrega</li>
        </ol>
        <p>
          <strong>Buena noticia:</strong> Con BlueCMR puede generar un único documento digital que cumple ambas funciones, ahorrando tiempo y papel.
        </p>
        <Callout title="Sustitución legal">
          <p>
            Una Carta de Porte <strong>digital</strong> que incluya todos los datos obligatorios del DeCA <strong>sustituye y cumple</strong> con la obligación legal. No hace falta llevar ambos documentos por separado.
          </p>
        </Callout>

        <h3>Transporte internacional por carretera</h3>
        <p>
          En un transporte internacional por carretera desde o hacia España:
        </p>
        <ul>
          <li><strong>CMR/eCMR</strong> - Contrato internacional de transporte (obligatorio según Convenio CMR)</li>
        </ul>
        <Callout title="¿Y el DeCA?">
          <p>
            La Orden FOM/2861/2012 <strong>no aplica a transportes internacionales por carretera</strong> (exportaciones e importaciones). El DeCA solo es necesario si el transporte se realiza <strong>íntegramente en territorio español</strong> (por ejemplo, un cabotaje realizado por un transportista extranjero dentro de España).
          </p>
        </Callout>
        <Callout title="Sustitución legal (para cabotajes)">
          <p>
            Si el transporte es un cabotaje en territorio español, un eCMR <strong>digital</strong> que incluya todos los datos obligatorios del DeCA <strong>sustituye y cumple</strong> con la obligación legal.
          </p>
        </Callout>

        <h3>¿Puedo usar solo la Carta de Porte?</h3>
        <p>
          <strong>No necesariamente por separado.</strong> Aunque la Carta de Porte formaliza el contrato, el DeCA es <strong>obligatorio por ley</strong> para los transportes de mercancías por carretera en España. Sin embargo, una Carta de Porte <strong>digital</strong> que incluya todos los datos obligatorios del DeCA <strong>sí sustituye y cumple</strong> con la obligación legal.
        </p>
        <p>
          <strong>Requisito técnico:</strong> Para que la sustitución sea válida, el documento digital debe cumplir los requisitos técnicos de la Resolución de 5/06/2026: formato <strong>PDF nativo digital</strong> (máx. 5 MB), <strong>código QR</strong> con URL HTTPS de descarga directa, y garantía de integridad y acceso verificable para los Servicios de Inspección.
        </p>
        <ArticleLink to="/documentos/documento-control" title="Guía del DeCA" text="¿Necesita información específica sobre el DeCA? Consulte nuestra guía completa." />

        <h2 id="casos-practicos">Casos prácticos</h2>

        <h3>Caso 1: Transporte Madrid → Barcelona</h3>
        <p>
          <strong>Situación:</strong> Transporta mercancía de Madrid a Barcelona (ambas en España).
        </p>
        <p><strong>Documentos necesarios:</strong></p>
        <Checks
          items={[
            'DeCA - Obligatorio por ley',
            'Carta de Porte - Para formalizar contrato y probar entrega',
          ]}
        />
        <p>
          Con BlueCMR genera ambos en un solo documento digital que cumple ambas funciones.
        </p>

        <h3>Caso 2: Transporte Madrid → París</h3>
        <p>
          <strong>Situación:</strong> Transporta mercancía de Madrid (España) a París (Francia).
        </p>
        <p><strong>Documento necesario:</strong></p>
        <Checks items={['CMR/eCMR - Contrato internacional (válido en España y Francia)']} />
        <Callout>
          <p>
            El CMR debe viajar en cabina según Reglamento CE 1072/2009. Al tratarse de un transporte internacional (exportación), <strong>no se requiere el DeCA</strong> regulado por la Orden FOM/2861/2012.
          </p>
        </Callout>

        <h2 id="tabla-comparativa">Tabla comparativa completa</h2>
        <DataTable
          head={['Característica', 'DeCA', 'Carta de Porte', 'CMR / eCMR']}
          rows={[
            ['Naturaleza', 'Administrativo', 'Contractual', 'Contractual'],
            ['Finalidad', 'Control e inspección por autoridades', 'Formalizar contrato de transporte', 'Contrato internacional de transporte'],
            ['Ámbito', 'Solo España', 'Nacional', 'Internacional'],
            ['Formato', 'Electrónico, obligatorio desde el 5 de octubre de 2026', 'Papel o digital', 'Papel (CMR) o digital (eCMR)'],
            ['¿Viaje en cabina obligatorio?', 'Sí', 'No', 'Sí (Reglamento CE 1072/2009)'],
            ['Partes del documento', 'Cargador contractual y transportista efectivo', 'Cargador, Porteador, Destinatario', 'Cargador, Porteador, Destinatario'],
            ['¿Firmas obligatorias?', 'No', 'Sí (cargador y porteador; destinatario al recibir)', 'Sí (expedidor y transportista; destinatario al recibir)'],
            ['¿Prueba la entrega?', 'No', 'Sí', 'Sí'],
          ]}
        />

        <h2 id="preguntas-frecuentes">Preguntas frecuentes</h2>
        <FaqList items={FAQ} />
        <ArticleLink
          to="/articulos/formato-pdfa1a-documento-control-digital-2026"
          title="Conozca los requisitos técnicos completos"
          text="Qué exige la Resolución de 5 de junio de 2026 al documento de control electrónico."
        />
      </ArticleLayout>
    </>
  );
};

export default DiferenciasDocumentosPage;
